import * as i0 from '@angular/core';
import { Injectable, InjectionToken, NgModule, Inject } from '@angular/core';
import { filter, catchError } from 'rxjs/operators';
import * as i1 from 'angular-oauth2-oidc';
import { OAuthModule } from 'angular-oauth2-oidc';
import * as i2 from '@angular/router';
import { throwError } from 'rxjs';
import { RequestTracingInterceptor, REQUEST_TRACING_CONFIG } from '@psu/utils/browser';

class AuthService {
    constructor(oauthService) {
        this.oauthService = oauthService;
        this.events = this.oauthService.events;
    }
    tryLogin(options) {
        return this.oauthService.tryLogin(options);
    }
    configure(config) {
        this.oauthService.configure(config);
    }
    setupAutomaticSilentRefresh(params = {}, listenTo, noPrompt = true) {
        this.oauthService.setupAutomaticSilentRefresh(params, listenTo, noPrompt);
    }
    initLoginFlow(additionalState = '', params = {}) {
        this.oauthService.initLoginFlow(additionalState, params);
    }
    isLoggedIn() {
        if (this.oauthService.responseType && this.oauthService.responseType.includes('id_token')) {
            return this.hasValidAccessToken() && this.hasValidIdToken();
        }
        else {
            return this.hasValidAccessToken();
        }
    }
    hasValidAccessToken() {
        return this.oauthService.hasValidAccessToken();
    }
    hasValidIdToken() {
        return this.oauthService.hasValidIdToken();
    }
    silentRefresh() {
        this.oauthService.silentRefresh();
    }
    loadDiscoveryDocument(fullUrl) {
        return this.oauthService.loadDiscoveryDocument(fullUrl);
    }
    enableDebug() {
        this.oauthService.events.subscribe((e) => {
            // eslint-disable-next-line no-console
            console.debug('oauth/oidc event', e);
        });
        this.oauthService.events.pipe(filter((e) => e.type === 'session_terminated')).subscribe((e) => {
            // eslint-disable-next-line no-console
            console.debug('Your session has been terminated!');
        });
    }
    getCurrentUser() {
        const identityClaims = this.oauthService.getIdentityClaims();
        return !!identityClaims
            ? {
                userName: identityClaims['sub'],
                accountType: identityClaims['accountType'],
                displayName: identityClaims['displayName'],
                email: identityClaims['email'],
                psuid: identityClaims['psuid'],
            }
            : null;
    }
}
AuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthService, deps: [{ token: i1.OAuthService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.OAuthService }]; } });

class AnonymousGuard {
    constructor(authService, router) {
        this.authService = authService;
        this.router = router;
    }
    /**
     * If there is an authenticated user, navigates to the configured page.
     */
    canActivate(route, state) {
        return this.checkAuth(route.data);
    }
    canActivateChild(childRoute, state) {
        return this.checkAuth(childRoute.data);
    }
    checkAuth(data) {
        if (!data['targetRoute']) {
            console.warn('AnonymousGuard must have a targetRoute property specified on the route data, but none was found');
        }
        const authenticated = this.authService.hasValidAccessToken() && this.authService.hasValidIdToken();
        if (authenticated) {
            return this.router.parseUrl(data['targetRoute']);
        }
        else {
            return true;
        }
    }
}
AnonymousGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AnonymousGuard, deps: [{ token: AuthService }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Injectable });
AnonymousGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AnonymousGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AnonymousGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: AuthService }, { type: i2.Router }]; } });

class AuthStorage {
    constructor(oauthStorage) {
        this.oauthStorage = oauthStorage;
    }
    getItem(key) {
        return this.oauthStorage.getItem(key);
    }
}
AuthStorage.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthStorage, deps: [{ token: i1.OAuthStorage }], target: i0.ɵɵFactoryTarget.Injectable });
AuthStorage.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthStorage, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthStorage, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.OAuthStorage }]; } });

class AuthGuard {
    constructor(authService) {
        this.authService = authService;
    }
    canActivate(route, state) {
        return this.checkAuth(state);
    }
    canActivateChild(childRoute, state) {
        return this.checkAuth(state);
    }
    checkAuth(state) {
        if (this.authService.hasValidAccessToken() && this.authService.hasValidIdToken()) {
            return true;
        }
        else {
            this.authService.initLoginFlow(state.url);
            return false;
        }
    }
}
AuthGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthGuard, deps: [{ token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: AuthService }]; } });

const REQUIRE_AUTH_HEADER = 'X-Auth';
const HTTP_AUTHORIZATION_HEADER = 'Authorization';

class AuthInterceptor {
    constructor(authStorage) {
        this.authStorage = authStorage;
    }
    intercept(req, next) {
        // figure out if the request requires authentication.
        const shouldAuth = this.requiresAuth(req);
        // always remove the custom x-auth header (cases where token handler cannot handle a request)
        const headers = req.headers.delete(REQUIRE_AUTH_HEADER);
        const newReq = req.clone({ headers });
        if (shouldAuth) {
            const token = this.authStorage.getItem('access_token');
            return next
                .handle(newReq.clone({
                setHeaders: {
                    [HTTP_AUTHORIZATION_HEADER]: `Bearer ${token}`,
                },
            }))
                .pipe(catchError((err) => throwError(err)));
        }
        else {
            return next.handle(newReq);
        }
    }
    requiresAuth(req) {
        // 1. Does request have our custom X-Auth header?
        // 2. Is value of X-Auth header 'true'?
        // 3. Does request NOT already have an Authorization header?
        // If both are true, we can handle this request
        return (req.headers.has(REQUIRE_AUTH_HEADER) &&
            req.headers.get(REQUIRE_AUTH_HEADER) === 'true' &&
            !req.headers.has(HTTP_AUTHORIZATION_HEADER));
    }
}
AuthInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthInterceptor, deps: [{ token: AuthStorage }], target: i0.ɵɵFactoryTarget.Injectable });
AuthInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AuthStorage }]; } });

const AUTH_CONFIG = new InjectionToken('auth.config');
class AuthConfig {
    constructor(json) {
        /**
         * The client's id as registered with the auth server
         */
        this.clientId = '';
        /**
         * The client's redirectUri as registered with the auth server
         */
        this.redirectUri = '';
        /**
         * An optional second redirectUri where the auth server
         * redirects the user to after logging out.
         */
        this.postLogoutRedirectUri = '';
        /**
         * Defines whether to use 'redirectUri' as a replacement
         * of 'postLogoutRedirectUri' if the latter is not set.
         */
        this.redirectUriAsPostLogoutRedirectUriFallback = true;
        /**
         * The auth server's endpoint that allows to log
         * the user in when using implicit flow.
         */
        this.loginUrl = '';
        /**
         * The requested scopes
         */
        this.scope = 'openid profile';
        this.resource = '';
        this.rngUrl = '';
        /**
         * Defines whether to use OpenId Connect during
         * implicit flow.
         */
        this.oidc = true;
        /**
         * Defines whether to request an access token during
         * implicit flow.
         */
        this.requestAccessToken = true;
        this.options = null;
        /**
         * The issuer's uri.
         */
        this.issuer = '';
        /**
         * The logout url.
         */
        this.logoutUrl = '';
        /**
         * Defines whether to clear the hash fragment after logging in.
         */
        this.clearHashAfterLogin = true;
        /**
         * Url of the token endpoint as defined by OpenId Connect and OAuth 2.
         */
        this.tokenEndpoint = undefined;
        /**
         * Url of the revocation endpoint as defined by OpenId Connect and OAuth 2.
         */
        this.revocationEndpoint = undefined;
        /**
         * Names of known parameters sent out in the TokenResponse. https://tools.ietf.org/html/rfc6749#section-5.1
         */
        this.customTokenParameters = [];
        /**
         * Url of the userinfo endpoint as defined by OpenId Connect.
         */
        this.userinfoEndpoint = undefined;
        this.responseType = '';
        /**
         * Defines whether additional debug information should
         * be shown at the console. Note that in certain browsers
         * the verbosity of the console needs to be explicitly set
         * to include Debug level messages.
         */
        this.showDebugInformation = false;
        /**
         * The redirect uri used when doing silent refresh.
         */
        this.silentRefreshRedirectUri = '';
        this.silentRefreshMessagePrefix = '';
        /**
         * Set this to true to display the iframe used for
         * silent refresh for debugging.
         */
        this.silentRefreshShowIFrame = false;
        /**
         * Timeout for silent refresh.
         *
         * @internal
         * depreacted b/c of typo, see silentRefreshTimeout
         */
        this.siletRefreshTimeout = 1000 * 20;
        /**
         * Timeout for silent refresh.
         */
        this.silentRefreshTimeout = 1000 * 20;
        /**
         * Some auth servers don't allow using password flow
         * w/o a client secret while the standards do not
         * demand for it. In this case, you can set a password
         * here. As this password is exposed to the public
         * it does not bring additional security and is therefore
         * as good as using no password.
         */
        this.dummyClientSecret = undefined;
        /**
         * Defines whether https is required.
         * The default value is remoteOnly which only allows
         * http for localhost, while every other domains need
         * to be used with https.
         */
        this.requireHttps = 'remoteOnly';
        /**
         * Defines whether every url provided by the discovery
         * document has to start with the issuer's url.
         */
        this.strictDiscoveryDocumentValidation = true;
        /**
         * JSON Web Key Set (https://tools.ietf.org/html/rfc7517)
         * with keys used to validate received id_tokens.
         * This is taken out of the discovery document. Can be set manually too.
         */
        this.jwks = undefined;
        /**
         * Map with additional query parameter that are appended to
         * the request when initializing implicit flow.
         */
        this.customQueryParams = undefined;
        this.silentRefreshIFrameName = 'angular-oauth-oidc-silent-refresh-iframe';
        /**
         * Defines when the token_timeout event should be raised.
         * If you set this to the default value 0.75, the event
         * is triggered after 75% of the token's life time.
         */
        this.timeoutFactor = 0.75;
        /**
         * If true, the lib will try to check whether the user
         * is still logged in on a regular basis as described
         * in http://openid.net/specs/openid-connect-session-1_0.html#ChangeNotification
         */
        this.sessionChecksEnabled = false;
        /**
         * Interval in msec for checking the session
         * according to http://openid.net/specs/openid-connect-session-1_0.html#ChangeNotification
         */
        this.sessionCheckIntervall = 3 * 1000;
        /**
         * Url for the iframe used for session checks
         */
        this.sessionCheckIFrameUrl = undefined;
        /**
         * Name of the iframe to use for session checks
         */
        this.sessionCheckIFrameName = 'angular-oauth-oidc-check-session-iframe';
        /**
         * This property has been introduced to disable at_hash checks
         * and is indented for Identity Provider that does not deliver
         * an at_hash EVEN THOUGH its recommended by the OIDC specs.
         * Of course, when disabling these checks then we are bypassing
         * a security check which means we are more vulnerable.
         */
        this.disableAtHashCheck = false;
        /**
         * Defines wether to check the subject of a refreshed token after silent refresh.
         * Normally, it should be the same as before.
         */
        this.skipSubjectCheck = false;
        this.useIdTokenHintForSilentRefresh = false;
        /**
         * Defined whether to skip the validation of the issuer in the discovery document.
         * Normally, the discovey document's url starts with the url of the issuer.
         */
        this.skipIssuerCheck = false;
        /**
         * final state sent to issuer is built as follows:
         * state = nonce + nonceStateSeparator + additional state
         * Default separator is ';' (encoded %3B).
         * In rare cases, this character might be forbidden or inconvenient to use by the issuer so it can be customized.
         */
        this.nonceStateSeparator = ';';
        /**
         * Set this to true to use HTTP BASIC auth for AJAX calls
         */
        this.useHttpBasicAuth = false;
        /**
         * The interceptors waits this time span if there is no token
         */
        this.waitForTokenInMsec = 0;
        /**
         * Code Flow is by defauld used together with PKCI which is also higly recommented.
         * You can disbale it here by setting this flag to true.
         * https://tools.ietf.org/html/rfc7636#section-1.1
         */
        this.disablePKCE = false;
        /**
         * Set this to true to preserve the requested route including query parameters after code flow login.
         * This setting enables deep linking for the code flow.
         */
        this.preserveRequestedRoute = false;
        /**
         * This property allows you to override the method that is used to open the login url,
         * allowing a way for implementations to specify their own method of routing to new
         * urls.
         */
        this.openUri = (uri) => {
            location.href = uri;
        };
        if (json) {
            Object.assign(this, json);
        }
    }
}
const AUTH_CONFIG_DD = new InjectionToken('auth.config.dd');
/**
 * Additional options that can be passed to tryLogin.
 */
class LoginOptions {
    constructor() {
        /**
         * Set this to true to disable the nonce
         * check which is used to avoid
         * replay attacks.
         * This flag should never be true in
         * production environments.
         */
        this.disableNonceCheck = false;
        /**
         * Normally, you want to clear your hash fragment after
         * the lib read the token(s) so that they are not displayed
         * anymore in the url. If not, set this to true. For code flow
         * this controls removing query string values.
         */
        this.preventClearHashAfterLogin = false;
    }
}
/**
 * Represents the received tokens, the received state
 * and the parsed claims from the id-token.
 */
class ReceivedTokens {
    constructor() {
        this.idToken = '';
        this.accessToken = '';
    }
}
class OAuthEvent {
    constructor(type) {
        this.type = type;
    }
}
class OAuthSuccessEvent extends OAuthEvent {
    constructor(type, info = null) {
        super(type);
        this.info = info;
    }
}
class OAuthInfoEvent extends OAuthEvent {
    constructor(type, info = null) {
        super(type);
        this.info = info;
    }
}
class OAuthErrorEvent extends OAuthEvent {
    constructor(type, reason, params = null) {
        super(type);
        this.reason = reason;
        this.params = params;
    }
}

class AuthModule {
}
AuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AuthModule, imports: [i1.OAuthModule] });
AuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthModule, imports: [OAuthModule.forRoot()] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [OAuthModule.forRoot()],
                }]
        }] });

class UserIdRequestTracingInterceptor extends RequestTracingInterceptor {
    constructor(config, authStorage) {
        super(config);
        this.config = config;
        this.authStorage = authStorage;
    }
    generateRequestId() {
        // Have to get userId like this because of circular dependency issue with injecting OAuthService
        const claimStr = this.authStorage.getItem('id_token_claims_obj');
        const claims = claimStr ? JSON.parse(claimStr) : null;
        const userId = claims ? claims.sub : null;
        if (!!userId) {
            return this.generate(userId);
        }
        else {
            return super.generateRequestId();
        }
    }
    generate(userid) {
        return `${super.getApplicationId()}_${userid}_${super.getShortUuid()}`;
    }
}
UserIdRequestTracingInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: UserIdRequestTracingInterceptor, deps: [{ token: REQUEST_TRACING_CONFIG }, { token: AuthStorage }], target: i0.ɵɵFactoryTarget.Injectable });
UserIdRequestTracingInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: UserIdRequestTracingInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: UserIdRequestTracingInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [REQUEST_TRACING_CONFIG]
                }] }, { type: AuthStorage }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_CONFIG, AUTH_CONFIG_DD, AnonymousGuard, AuthConfig, AuthGuard, AuthInterceptor, AuthModule, AuthService, AuthStorage, HTTP_AUTHORIZATION_HEADER, LoginOptions, OAuthErrorEvent, OAuthEvent, OAuthInfoEvent, OAuthSuccessEvent, REQUIRE_AUTH_HEADER, ReceivedTokens, UserIdRequestTracingInterceptor };
